import React, { useState, useEffect, useMemo } from "react";
import { Stack } from "react-bootstrap";
import { formatDistanceToNowStrict, parseISO, isValid } from "date-fns";
import ArrowRight from "@/assets/ArrowRight.svg";
import useFilteredJobs from "@/hooks/useFilteredJobs";
import styles from "./styles.module.scss";
import { trimText } from "@/functions/trimText";

// --- DATA IMPORTS ---
import RawBranches from "@/../.sourceflow/jobs-Branches.json";
import RawJobType from "@/../.sourceflow/jobs-Job Type.json";
import RawSector from "@/../.sourceflow/jobs-Sector.json";
import RawSalary from "@/../.sourceflow/jobs-Salary.json";
import RawWorkType from "@/../.sourceflow/jobs-Work type.json";
import RawHoursType from "@/../.sourceflow/jobs-FullPart Time.json";
import RawEmployers from "@/../.sourceflow/employers.json";

// --- HELPERS ---
const normalizeTextForSearch = (text = "") => {
  if (typeof text !== "string") return "";
  return text.toLowerCase().replace(/[-.,]/g, " ").replace(/\s\s+/g, " ").trim();
};

const removeEmojis = (text) => {
  if (typeof text !== "string") return "";
  return text
    .replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      "",
    )
    .trim();
};

const formatSalary = (salary) => {
  if (!salary) return "";
  return salary.includes("£") || salary.includes("$") ? salary : `£${salary}`;
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  if ([lat1, lon1, lat2, lon2].some((coord) => Number.isNaN(coord) || coord === null)) return Infinity;
  const R = 3958.8; // miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const normalizeAndExtractItems = (rawData) => {
  const items = rawData?.category_values || [];
  return items
    .map((item) => {
      if (!item || !item.id) return null;
      const nameKey = Object.keys(item).find((key) => item[key] && typeof item[key].en !== "undefined");
      if (nameKey) return { id: item.id, name: item[nameKey].en };
      return null;
    })
    .filter(Boolean);
};

const Branches = normalizeAndExtractItems(RawBranches);
const JobType = normalizeAndExtractItems(RawJobType);
const Sector = normalizeAndExtractItems(RawSector);
const Salary = normalizeAndExtractItems(RawSalary);
const WorkType = normalizeAndExtractItems(RawWorkType);
const HoursType = normalizeAndExtractItems(RawHoursType);
const fullBranchData = RawBranches?.category_values || [];

const CATEGORY_IDS = {
  branches: "33b21b47-15e3-40a2-bc7b-32019e94c7ba",
};

const CATEGORY_MAP = {
  Sector: { items: Sector, categoryId: "3186657c-e89c-4a6f-9157-35eb7fe0b379" },
  "Job Type": { items: JobType, categoryId: "275d8990-bd9e-4f79-a0e2-d81bb734c855" },
  Salary: { items: Salary, categoryId: "0b2aa473-88d3-4144-a158-6f8c355bf042" },
  Branches: { items: Branches, categoryId: "33b21b47-15e3-40a2-bc7b-32019e94c7ba" },
  "Work type": { items: WorkType, categoryId: "1bfde1ab-62a6-4040-9c0e-5b8c59d4f6d8" },
  "Full/Part Time": { items: HoursType, categoryId: "f2bec422-3deb-4111-ab36-89cb892e33a6" },
};

const employerLogoMap = new Map(
  RawEmployers.category_values?.filter((emp) => emp?.id && emp.logo?.en).map((emp) => [emp.id, emp.logo.en]) || [],
);

const LOCATION_ALIASES = {
  london: "city of london",
  manchester: "manchester",
  burton: "burton upon trent",
  "burton on trent": "burton upon trent",
};

const getJobCoords = (job) => {
  const preciseCoords = job.derived_info?.locations?.[0]?.lat_lng;
  if (preciseCoords?.latitude && preciseCoords?.longitude) {
    return { lat: Number(preciseCoords.latitude), lng: Number(preciseCoords.longitude) };
  }
  const branchCategory = job.categories?.find((c) => c.id === CATEGORY_IDS.branches);
  if (branchCategory?.values?.[0]?.id) {
    const branchId = branchCategory.values[0].id;
    const branch = fullBranchData.find((b) => b.id === branchId);
    if (branch?.latitude?.en && branch?.longitude?.en) {
      return { lat: Number(branch.latitude.en), lng: Number(branch.longitude.en) };
    }
  }
  return null;
};

// --- UI SUBCOMPONENTS ---
const JobSearchForm = ({ query, setQuery, location, setLocation, radius, setRadius, jobCount }) => (
  <form className={`${styles["js-form"]} ${styles["js-panel-border1"]}`} onSubmit={(e) => e.preventDefault()}>
    <div className={styles["js-total-count"]}>
      Search <span className={styles["js-total-count-value"]}>{jobCount}</span> vacancies
    </div>
    <div className={`${styles["js-form-input-wrapper"]} d-lg-flex`}>
      <div className={`${styles["js-input-wrapper"]} ${styles["js-input-wrapper-query"]}`}>
        <input
          className={styles["js-input"]}
          type="text"
          placeholder="Enter job title..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className={`${styles["js-input-wrapper"]} ${styles["js-input-wrapper-location"]}`}>
        <input
          className={styles["js-input"]}
          type="text"
          placeholder="e.g. Manchester, London"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
      </div>
      <div className={`${styles["js-input-wrapper"]} ${styles["js-input-wrapper-radius"]}`}>
        <select className={styles["js-input"]} value={radius} onChange={(e) => setRadius(e.target.value)}>
          {[5, 10, 20, 30, 50, 100].map((r) => (
            <option key={r} value={r}>
              {r} miles
            </option>
          ))}
        </select>
      </div>
      <button type="submit" className={styles["js-button"]}>
        <Stack className="flex-row align-items-center justify-content-center px-3" gap={2}>
          Search <ArrowRight />
        </Stack>
      </button>
    </div>
  </form>
);

const JobFilters = ({
  filters,
  openFilters,
  isMobileFilterOpen,
  filterCounts,
  handleFilterChange,
  toggleFilterCategory,
  toggleMobileFilter,
}) => (
  <div className={styles["js-filters-column"]}>
    <button
      type="button"
      className={`${styles["js-button"]} ${styles["js-filters-toggle-button"]} btn btn-primary`}
      onClick={toggleMobileFilter}
    >
      Filters
    </button>
    <div
      className={`${styles["js-filters-collapse"]} ${
        !isMobileFilterOpen ? styles["js-filters-closed"] : styles["js-filters-open"]
      }`}
    >
      {Object.entries(CATEGORY_MAP).map(([category, { items, categoryId }]) => {
        const isCategoryOpen = openFilters[categoryId];
        return (
          <div key={categoryId} className={`${styles["js-filter-box"]} ${styles["js-panel-border"]}`}>
            <div
              className={`${styles["js-filter-title"]} ${isCategoryOpen ? styles["js-filter-title-open"] : ""}`}
              onClick={() => toggleFilterCategory(categoryId)}
            >
              <div>
                <button className={styles["js-filter-title-button"]}>{category}</button>
                <span className={styles["js-filter-title-icon"]}></span>
              </div>
            </div>
            {isCategoryOpen && items.length > 0 && (
              <div className={styles["js-filter-category-open"]}>
                <ul className={styles["js-filter-values-section"]}>
                  {items.map((item) => {
                    if (!item || !item.name) return null;
                    const isChecked = filters[categoryId]?.includes(item.id) || false;
                    const count = filterCounts?.[categoryId]?.[item.id] || 0;
                    if (count === 0 && !isChecked) return null;
                    return (
                      <li
                        key={item.id}
                        className={styles["js-filter-value-list-item"]}
                        onClick={() => handleFilterChange(categoryId, item.id)}
                      >
                        <button className={styles["js-filter-value"]} value={item.id}>
                          <span
                            className={`${styles["js-filter-value-content"]} ${
                              isChecked ? styles["js-filter-selected-class"] : ""
                            }`}
                          >
                            <input type="checkbox" className="form-check-input" checked={isChecked} readOnly />
                            <span className={styles["js-filter-value-name"]}>{item.name}</span>
                            <span className={styles["js-filter-count"]}>{count}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const JobCard = ({ job }) => {
  const employerLogo = useMemo(() => {
    const employerCategory = job.categories?.find((cat) => cat && cat.name === "Employers");
    const employerId = employerCategory?.values?.[0]?.id;
    return employerId ? employerLogoMap.get(employerId) : null;
  }, [job.categories]);

  const jobTags = useMemo(() => {
    const tags = [];
    if (job.salary_package) tags.push(formatSalary(job.salary_package));
    const otherCategories =
      job.categories
        ?.filter((cat) => cat && cat.name !== "Salary" && cat.name !== "Currency")
        .flatMap((cat) => cat.values || [])
        .map((val) => val && val.name)
        .filter(Boolean) || [];
    tags.push(...otherCategories);
    return tags;
  }, [job.salary_package, job.categories]);

  const postedAt = useMemo(() => {
    const parsedDate = job.published_at ? parseISO(job.published_at) : null;
    return parsedDate && isValid(parsedDate) ? formatDistanceToNowStrict(parsedDate, { addSuffix: true }) : null;
  }, [job.published_at]);

  const slug = job.url_slug || job.id;

  return (
    <div className={`${styles["js-result"]} ${styles["js-panel-border"]}`}>
      <div className={`${styles["js-result-title"]} ${employerLogo ? styles["has-logo"] : ""}`}>
        <a href={`/jobs/${slug}`}>{job.title}</a>
      </div>
      <div className={styles["js-result-details"]}>
        {job.addresses?.[0] && <div className={styles["js-results-details-location"]}>{job.addresses[0]}</div>}
        {jobTags.map(
          (tag, index) =>
            tag && (
              <span key={index} className={styles["js-results-details-category"]}>
                {tag}
              </span>
            ),
        )}
      </div>
      {job.description && (
        <div
          className={styles["js-result-description"]}
          dangerouslySetInnerHTML={{ __html: trimText(removeEmojis(job.description), 300) }}
        />
      )}
      <div className={styles["js-result-links"]}>
        {employerLogo && (
          <img className={styles["js-results-details-employer-logo"]} src={employerLogo} alt={`${job.title} logo`} />
        )}
        {postedAt && <time className="me-auto">Posted {postedAt}</time>}
        <div className={styles["cta"]}>
          <a role="button" href={`/jobs/${slug}`} className="btn btn-secondary">
            View job <ArrowRight />
          </a>
        </div>
        <div className={styles["cta"]}>
          <a role="button" href={`/jobs/${slug}/#Apply`} className="btn btn-outline-secondary">
            Apply now <ArrowRight />
          </a>
        </div>
      </div>
    </div>
  );
};

const JobList = ({ jobs, visibleCount, setVisibleCount }) => (
  <div className={styles["js-results"]}>
    {jobs.slice(0, visibleCount).map((job) => (
      <JobCard key={job.id} job={job} />
    ))}
    {visibleCount < jobs.length && (
      <div className={styles["js-pagination"]}>
        <button className={styles["js-button"]} onClick={() => setVisibleCount((prev) => prev + 10)}>
          Load more jobs
        </button>
      </div>
    )}
  </div>
);

// --- MAIN ---
export default function JobsSearchFeed({
  sectors,
  jobType,
  workType,
  branches,
  changeURLOnFilterChange,
  initialQuery = "",
  initialRadius = "20",
  initialLocation = "",
  readURLOnMount = true,
  debugCounts = false,
  excludeQuery = "",
  exactQueryMatch,
}) {
  const [query, setQuery] = useState(initialQuery);
  const [location, setLocation] = useState(initialLocation);
  const [radius, setRadius] = useState(initialRadius);
  const [filters, setFilters] = useState({});
  const [visibleCount, setVisibleCount] = useState(10);
  const [openFilters, setOpenFilters] = useState(
    Object.fromEntries(Object.values(CATEGORY_MAP).map((cat) => [cat.categoryId, true])),
  );
  const [isMobileFilterOpen, setMobileFilterOpen] = useState(false);

  const allJobs = useFilteredJobs({ sectors, jobType, workType, branches });

  const textMatchesLocation = (job, term) => {
    if (!term) return false;
    const searchRegex = new RegExp(`\\b${term}\\b`, "i");
    return job.addresses?.some((addr) => searchRegex.test(normalizeTextForSearch(addr)));
  };

  const afterLocation = useMemo(() => {
    if (!allJobs) return [];
    let jobs = [...allJobs];

    if (!location) return jobs;

    const locationsToSearch = location
      .split(",")
      .map((term) => normalizeTextForSearch(term).trim())
      .filter(Boolean);

    if (locationsToSearch.length > 1) {
      const searchRegexes = locationsToSearch.map((loc) => new RegExp(`\\b${loc}\\b`, "i"));
      return jobs.filter((job) =>
        searchRegexes.some((regex) => job.addresses?.some((addr) => regex.test(normalizeTextForSearch(addr)))),
      );
    }

    const originalTerm = locationsToSearch[0];
    const aliasedTerm = LOCATION_ALIASES[originalTerm] || originalTerm;
    const locationData = fullBranchData.find((branch) => normalizeTextForSearch(branch.name?.en) === aliasedTerm);

    if (locationData?.latitude?.en && locationData?.longitude?.en) {
      const lat1 = Number(locationData.latitude.en);
      const lon1 = Number(locationData.longitude.en);
      let byRadius = jobs.filter((job) => {
        const jobCoords = getJobCoords(job);
        if (jobCoords && !Number.isNaN(jobCoords.lat) && !Number.isNaN(jobCoords.lng)) {
          const distance = haversineDistance(lat1, lon1, jobCoords.lat, jobCoords.lng);
          return distance <= Number(radius);
        }
        return textMatchesLocation(job, aliasedTerm) || textMatchesLocation(job, originalTerm);
      });

      if (byRadius.length === 0) {
        byRadius = jobs.filter(
          (job) => textMatchesLocation(job, aliasedTerm) || textMatchesLocation(job, originalTerm),
        );
      }
      return byRadius;
    }

    return jobs.filter((job) => textMatchesLocation(job, aliasedTerm) || textMatchesLocation(job, originalTerm));
  }, [allJobs, location, radius]);

  // --- UPDATED KEYWORD FILTER ---
  const afterKeyword = useMemo(() => {
    if (!afterLocation) return [];

    const q = (query || "").trim().toLowerCase();
    if (!q) return afterLocation;

    return afterLocation.filter((job) => {
      const title = job.title?.toLowerCase() || "";

      // Exact match mode
      if (exactQueryMatch) {
        return title.includes(q);
      }

      // Default behaviour (current logic)
      const keywords = q
        .split(/[, ]+/)
        .map((kw) => kw.trim())
        .filter(Boolean);

      return keywords.some((kw) => title.includes(kw));
    });
  }, [afterLocation, query, exactQueryMatch]);

  const afterExclusion = useMemo(() => {
    if (!afterKeyword) return [];
    const excludeKeywords = (excludeQuery || "")
      .toLowerCase()
      .split(/[, ]+/)
      .map((kw) => kw.trim())
      .filter(Boolean);
    if (excludeKeywords.length === 0) return afterKeyword;

    return afterKeyword.filter((job) => {
      // Exclusion search is on the title only
      const title = job.title?.toLowerCase() || "";
      return !excludeKeywords.some((kw) => title.includes(kw));
    });
  }, [afterKeyword, excludeQuery]);

  const displayedJobs = useMemo(() => {
    if (!afterExclusion) return [];
    const activeFilters = Object.entries(filters).filter(([, ids]) => ids.length > 0);
    if (activeFilters.length === 0) return afterExclusion;
    return afterExclusion.filter((job) =>
      activeFilters.every(([categoryId, selectedIds]) => {
        const jobCategory = job.categories?.find((cat) => cat && cat.id === categoryId);
        return jobCategory?.values?.some((jobValue) => jobValue && selectedIds.includes(jobValue.id));
      }),
    );
  }, [afterExclusion, filters]);

  useEffect(() => setVisibleCount(10), [displayedJobs]);

  useEffect(() => {
    if (!readURLOnMount || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get("query");
    const loc = params.get("location");
    const rad = params.get("radius");
    if (q) setQuery(q);
    if (loc) setLocation(loc);
    if (rad) setRadius(rad);
  }, [readURLOnMount]);

  useEffect(() => {
    if (!changeURLOnFilterChange || typeof window === "undefined") return;
    const params = new URLSearchParams();
    if (query) params.set("query", query);
    if (location) params.set("location", location);
    if (radius) params.set("radius", radius);
    const qs = params.toString();
    const path = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
    window.history.replaceState({}, "", path);
  }, [query, location, radius, changeURLOnFilterChange]);

  const filterCounts = useMemo(() => {
    const counts = {};
    (afterExclusion || []).forEach((job) => {
      job.categories?.forEach((cat) => {
        if (cat?.values) {
          cat.values.forEach((val) => {
            if (val) {
              if (!counts[cat.id]) counts[cat.id] = {};
              counts[cat.id][val.id] = (counts[cat.id][val.id] || 0) + 1;
            }
          });
        }
      });
    });
    return counts;
  }, [afterExclusion]);

  const handleFilterChange = (categoryId, valueId) => {
    setFilters((prev) => {
      const current = new Set(prev[categoryId] || []);
      current.has(valueId) ? current.delete(valueId) : current.add(valueId);
      return { ...prev, [categoryId]: Array.from(current) };
    });
  };

  const toggleFilterCategory = (categoryId) => setOpenFilters((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  const toggleMobileFilter = () => setMobileFilterOpen((prev) => !prev);

  useEffect(() => {
    if (!debugCounts) return;
    const counts = {
      raw: allJobs?.length ?? 0,
      afterLocation: afterLocation?.length ?? 0,
      afterKeyword: afterKeyword?.length ?? 0,
      afterExclusion: afterExclusion?.length ?? 0,
      afterCategoryFilters: displayedJobs?.length ?? 0,
      query,
      location,
      radius,
      activeFilterKeys: Object.keys(filters).filter((k) => (filters[k] || []).length > 0),
    };
  }, [
    debugCounts,
    allJobs,
    afterLocation,
    afterKeyword,
    afterExclusion,
    displayedJobs,
    query,
    location,
    radius,
    filters,
  ]);

  return (
    <div className="py-5">
      <div className="container">
        {debugCounts && (
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
            <strong>Debug:</strong> raw={allJobs?.length ?? 0} → loc={afterLocation?.length ?? 0} → q=
            {afterKeyword?.length ?? 0} → exclude={afterExclusion?.length ?? 0} → final=
            {displayedJobs?.length ?? 0}
            {query ? ` | query="${query}"` : ""}
            {location ? ` | location="${location}"` : ""}
            {radius ? ` | r=${radius}` : ""}
          </div>
        )}
        <div className={styles["js-container"]}>
          <JobSearchForm
            {...{ query, setQuery, location, setLocation, radius, setRadius, jobCount: displayedJobs.length }}
          />
          <div className={styles["js-columns-container"]}>
            <JobFilters
              {...{
                filters,
                openFilters,
                isMobileFilterOpen,
                filterCounts,
                handleFilterChange,
                toggleFilterCategory,
                toggleMobileFilter,
              }}
            />
            <JobList jobs={displayedJobs} visibleCount={visibleCount} setVisibleCount={setVisibleCount} />
          </div>
        </div>
      </div>
    </div>
  );
}
